interface PerformanceMetric {
  name: string;
  duration: number;
  timestamp: number;
  type: "api" | "component" | "navigation" | "custom";
  metadata?: Record<string, any>;
}

interface ErrorMetric {
  id: string;
  message: string;
  stack?: string;
  timestamp: number;
  type: "javascript" | "api" | "network" | "boundary";
  severity: "low" | "medium" | "high" | "critical";
  metadata?: Record<string, any>;
  userAgent: string;
  url: string;
}

interface UserSession {
  sessionId: string;
  startTime: number;
  userId?: string;
  userAgent: string;
  errors: ErrorMetric[];
  performance: PerformanceMetric[];
}

class ErrorMonitoringService {
  private session: UserSession;
  private maxStoredErrors = 50;
  private maxStoredMetrics = 100;
  private batchQueue: ErrorMetric[] = [];
  private performanceQueue: PerformanceMetric[] = [];
  private batchTimeout: NodeJS.Timeout | null = null;
  private saveTimeout: NodeJS.Timeout | null = null;
  private readonly BATCH_SIZE = 10;
  private readonly BATCH_TIMEOUT = 5000; // 5 segundos
  private readonly SAVE_DEBOUNCE = 1000; // 1 segundo

  constructor() {
    this.session = {
      sessionId: this.generateSessionId(),
      startTime: Date.now(),
      userAgent: navigator.userAgent,
      errors: [],
      performance: [],
    };

    this.initializeErrorListeners();
    this.initializePerformanceObserver();
    this.initializeBatchProcessing();
  }

  private generateSessionId(): string {
    return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializeErrorListeners() {
    // Capturar erros JavaScript globais
    window.addEventListener("error", (event) => {
      this.logError({
        id: this.generateErrorId(),
        message: event.message,
        stack: event.error?.stack,
        timestamp: Date.now(),
        type: "javascript",
        severity: "high",
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
        userAgent: navigator.userAgent,
        url: window.location.href,
      });
    });

    // Capturar promises rejeitadas não tratadas
    window.addEventListener("unhandledrejection", (event) => {
      this.logError({
        id: this.generateErrorId(),
        message: event.reason?.message || "Unhandled Promise Rejection",
        stack: event.reason?.stack,
        timestamp: Date.now(),
        type: "javascript",
        severity: "medium",
        metadata: {
          reason: event.reason,
        },
        userAgent: navigator.userAgent,
        url: window.location.href,
      });
    });
  }

  private initializePerformanceObserver() {
    // Observer para navegação
    if ("PerformanceObserver" in window) {
      try {
        const navigationObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (entry.entryType === "navigation") {
              const navEntry = entry as PerformanceNavigationTiming;
              this.queuePerformanceMetric({
                name: "page-load",
                duration: navEntry.loadEventEnd - navEntry.loadEventStart,
                timestamp: Date.now(),
                type: "navigation",
                metadata: {
                  domContentLoaded:
                    navEntry.domContentLoadedEventEnd -
                    navEntry.domContentLoadedEventStart,
                  firstPaint:
                    navEntry.domContentLoadedEventEnd - navEntry.fetchStart,
                  navigationType: navEntry.type,
                },
              });
            }
          });
        });

        navigationObserver.observe({ entryTypes: ["navigation"] });

        // Observer para recursos (com throttling)
        const resourceObserver = new PerformanceObserver((list) => {
          const entries = list
            .getEntries()
            .filter((entry) => entry.duration > 1000); // Apenas recursos lentos

          entries.forEach((entry) => {
            this.queuePerformanceMetric({
              name: entry.name,
              duration: entry.duration,
              timestamp: Date.now(),
              type: "api",
              metadata: {
                transferSize: (entry as any).transferSize,
                initiatorType: (entry as any).initiatorType,
              },
            });
          });
        });

        resourceObserver.observe({ entryTypes: ["resource"] });
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.warn(
            "Performance Observer not supported or failed to initialize:",
            error
          );
        }
      }
    }
  }

  private initializeBatchProcessing() {
    // Processar batch a cada 5 segundos ou quando atingir limite
    this.batchTimeout = setInterval(() => {
      this.processBatches();
    }, this.BATCH_TIMEOUT);

    // Processar batch quando página for fechada
    window.addEventListener("beforeunload", () => {
      this.processBatches();
    });
  }

  private queuePerformanceMetric(metric: PerformanceMetric) {
    this.performanceQueue.push(metric);

    if (this.performanceQueue.length >= this.BATCH_SIZE) {
      this.processBatches();
    }
  }

  private processBatches() {
    // Processar batch de erros
    if (this.batchQueue.length > 0) {
      const errorsToProcess = this.batchQueue.splice(0);
      errorsToProcess.forEach((error) => this.processError(error));
    }

    // Processar batch de métricas de performance
    if (this.performanceQueue.length > 0) {
      const metricsToProcess = this.performanceQueue.splice(0);
      metricsToProcess.forEach((metric) =>
        this.processPerformanceMetric(metric)
      );
    }
  }

  private processError(error: ErrorMetric) {
    // Adicionar à sessão atual
    this.session.errors.push(error);

    // Manter apenas os últimos erros
    if (this.session.errors.length > this.maxStoredErrors) {
      this.session.errors = this.session.errors.slice(-this.maxStoredErrors);
    }

    // Log no console em desenvolvimento (throttled)
    if (process.env.NODE_ENV === "development" && Math.random() < 0.1) {
      // 10% chance
      console.group(`🚨 Error Logged: ${error.type}`);
      console.error("Message:", error.message);
      console.error("Stack:", error.stack);
      console.error("Metadata:", error.metadata);
      console.groupEnd();
    }
  }

  private processPerformanceMetric(metric: PerformanceMetric) {
    this.session.performance.push(metric);

    // Manter apenas as últimas métricas
    if (this.session.performance.length > this.maxStoredMetrics) {
      this.session.performance = this.session.performance.slice(
        -this.maxStoredMetrics
      );
    }

    // Log no console em desenvolvimento para métricas lentas (throttled)
    if (
      process.env.NODE_ENV === "development" &&
      metric.duration > 500 &&
      Math.random() < 0.2
    ) {
      // 20% chance para métricas lentas
      console.warn(
        `Slow ${metric.type}: ${metric.name} - ${metric.duration.toFixed(2)}ms`
      );
    }
  }

  private generateErrorId(): string {
    return `err-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  public logError(error: ErrorMetric) {
    // Adicionar à fila de batch em vez de processar imediatamente
    this.batchQueue.push(error);

    // Processar batch se atingiu o limite
    if (this.batchQueue.length >= this.BATCH_SIZE) {
      this.processBatches();
    }

    // Salvar no localStorage com debounce
    this.scheduleLocalStorageSave();

    // Em produção, enviar para serviço de monitoramento (também em batch)
    if (process.env.NODE_ENV === "production") {
      this.sendToMonitoringService(error);
    }
  }

  public logPerformance(metric: PerformanceMetric) {
    // Usar fila de batch em vez de processamento imediato
    this.queuePerformanceMetric(metric);
  }

  public logApiCall(
    url: string,
    method: string,
    duration: number,
    status?: number,
    error?: any
  ) {
    const isError = status && status >= 400;

    if (isError) {
      this.logError({
        id: this.generateErrorId(),
        message: `API Error: ${method} ${url} - Status ${status}`,
        timestamp: Date.now(),
        type: "api",
        severity: status >= 500 ? "high" : "medium",
        metadata: {
          url,
          method,
          status,
          duration,
          error: error?.message,
        },
        userAgent: navigator.userAgent,
        url: window.location.href,
      });
    }

    this.logPerformance({
      name: `${method} ${url}`,
      duration,
      timestamp: Date.now(),
      type: "api",
      metadata: {
        method,
        status,
        isError,
      },
    });
  }

  public logComponentError(
    componentName: string,
    error: Error,
    errorInfo?: any
  ) {
    this.logError({
      id: this.generateErrorId(),
      message: `Component Error in ${componentName}: ${error.message}`,
      stack: error.stack,
      timestamp: Date.now(),
      type: "boundary",
      severity: "high",
      metadata: {
        componentName,
        errorInfo,
      },
      userAgent: navigator.userAgent,
      url: window.location.href,
    });
  }

  public logCustomEvent(
    name: string,
    data?: any,
    type: "performance" | "error" = "performance"
  ) {
    if (type === "performance") {
      this.logPerformance({
        name,
        duration: 0,
        timestamp: Date.now(),
        type: "custom",
        metadata: data,
      });
    } else {
      this.logError({
        id: this.generateErrorId(),
        message: name,
        timestamp: Date.now(),
        type: "custom" as any,
        severity: "low",
        metadata: data,
        userAgent: navigator.userAgent,
        url: window.location.href,
      });
    }
  }

  private saveToLocalStorage() {
    try {
      localStorage.setItem(
        "errorMonitoringSession",
        JSON.stringify(this.session)
      );
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Failed to save monitoring data to localStorage:", error);
      }
    }
  }

  private scheduleLocalStorageSave() {
    // Debounce o salvamento no localStorage
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    this.saveTimeout = setTimeout(() => {
      this.saveToLocalStorage();
      this.saveTimeout = null;
    }, this.SAVE_DEBOUNCE);
  }

  private async sendToMonitoringService(error: ErrorMetric) {
    try {
      // Aqui você pode integrar com serviços como:
      // - Sentry
      // - LogRocket
      // - DataDog
      // - Seu próprio serviço de logging

      // await fetch('/api/monitoring/errors', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify({
      //     session: this.session.sessionId,
      //     error,
      //   }),
      // });

      console.info("Error sent to monitoring service:", error.id);
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Failed to send error to monitoring service:", err);
      }
    }
  }

  public getErrorReport(): UserSession {
    return { ...this.session };
  }

  public getErrorSummary() {
    const errors = this.session.errors;
    const criticalErrors = errors.filter(
      (e) => e.severity === "critical"
    ).length;
    const highErrors = errors.filter((e) => e.severity === "high").length;
    const apiErrors = errors.filter((e) => e.type === "api").length;
    const jsErrors = errors.filter((e) => e.type === "javascript").length;

    const avgApiTime = this.session.performance
      .filter((p) => p.type === "api")
      .reduce((acc, p, _, arr) => acc + p.duration / arr.length, 0);

    return {
      totalErrors: errors.length,
      criticalErrors,
      highErrors,
      apiErrors,
      jsErrors,
      averageApiTime: Math.round(avgApiTime),
      sessionDuration: Date.now() - this.session.startTime,
    };
  }

  public clearData() {
    this.session.errors = [];
    this.session.performance = [];
    this.batchQueue = [];
    this.performanceQueue = [];
    localStorage.removeItem("errorMonitoringSession");
  }

  public destroy() {
    // Limpar timeouts
    if (this.batchTimeout) {
      clearInterval(this.batchTimeout);
      this.batchTimeout = null;
    }

    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }

    // Processar batches finais
    this.processBatches();

    // Salvar dados finais
    this.saveToLocalStorage();

    // Limpar dados
    this.clearData();
  }

  public setUserId(userId: string) {
    this.session.userId = userId;
    this.saveToLocalStorage();
  }
}

// Instância singleton
export const errorMonitor = new ErrorMonitoringService();

// Hook para usar o monitoring em componentes React
export const useErrorMonitoring = () => {
  const logComponentError = (error: Error, errorInfo?: any) => {
    const componentName =
      (error as any).componentStack?.split("\n")[1] || "Unknown Component";
    errorMonitor.logComponentError(componentName, error, errorInfo);
  };

  const logCustomEvent = (name: string, data?: any) => {
    errorMonitor.logCustomEvent(name, data);
  };

  const getErrorSummary = () => {
    return errorMonitor.getErrorSummary();
  };

  return {
    logComponentError,
    logCustomEvent,
    getErrorSummary,
    clearData: errorMonitor.clearData.bind(errorMonitor),
    setUserId: errorMonitor.setUserId.bind(errorMonitor),
  };
};

// Integração com React DevTools
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  (window as any).__ERROR_MONITOR__ = errorMonitor;
}
